import { useEffect, useMemo, useState } from "react";

const normalizePhone = (phone) => String(phone || "").replace(/\D/g, "").slice(-10);

export default function LeadOtpVerification({
  phone,
  onVerifiedChange,
  className = "",
  otpEnabled = true,
  inlinePhoneField = false,
  onPhoneChange,
  phoneInputName = "phone",
  phonePlaceholder = "Phone Number",
  countryCodePrefix = "",
  phoneInputClassName = "",
}) {
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const [sessionId, setSessionId] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otpChannel, setOtpChannel] = useState("whatsapp");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const expectedOtpLength = otpChannel === "guest" ? 4 : 6;

  useEffect(() => {
    if (typeof onVerifiedChange === "function") {
      onVerifiedChange(otpEnabled ? isOtpVerified : normalizedPhone.length === 10);
    }
  }, [isOtpVerified, onVerifiedChange, otpEnabled, normalizedPhone]);

  useEffect(() => {
    setSessionId("");
    setOtp("");
    setIsOtpSent(false);
    setIsOtpVerified(false);
    setOtpChannel("whatsapp");
    setError("");
    setMessage("");
  }, [normalizedPhone]);

  const readErrorMessage = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.message || data?.error || fallbackMessage;
    } catch (parseError) {
      return fallbackMessage;
    }
  };

  const sendGuestOtp = async () => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (!apiUrl) {
      throw new Error("NEXT_PUBLIC_API_URL is not configured.");
    }

    const response = await fetch(`${apiUrl}/auth/requestGuestOtp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mobile: normalizedPhone }),
    });

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Unable to send OTP via SMS.")
      );
    }

    setSessionId("");
    setOtpChannel("guest");
    setIsOtpSent(true);
    setIsOtpVerified(false);
    setOtp("");
  };

  const resendViaSms = async () => {
    if (isOtpVerified) {
      return;
    }

    if (normalizedPhone.length !== 10) {
      setError("Please enter a valid 10-digit phone number first.");
      return;
    }

    setIsSending(true);
    setError("");
    setMessage("");

    try {
      await sendGuestOtp();
      setMessage("Didn't receive the WhatsApp OTP? We sent a new OTP via SMS.");
    } catch (err) {
      setError(err.message || "Unable to send OTP via SMS.");
    } finally {
      setIsSending(false);
    }
  };

  const renderSmsResendLink = () => {
    if (!isOtpSent || isOtpVerified || otpChannel === "guest") {
      return null;
    }

    return (
      <button
        type="button"
        onClick={resendViaSms}
        disabled={isSending}
        className="mt-2 text-xs font-medium text-[#0e605b] underline disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Didn&apos;t receive the OTP? Resend via SMS
      </button>
    );
  };

  const sendOtp = async () => {
    if (isOtpVerified) {
      return;
    }

    if (normalizedPhone.length !== 10) {
      setError("Please enter a valid 10-digit phone number first.");
      return;
    }

    setIsSending(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/otp/send-whatsapp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone: normalizedPhone }),
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.sessionId);
        setOtpChannel("whatsapp");
        setIsOtpSent(true);
        setIsOtpVerified(false);
        setOtp("");
        setMessage("OTP sent to your WhatsApp number.");
        return;
      }

      const whatsappError = await readErrorMessage(
        response,
        "Unable to send OTP on WhatsApp."
      );

      try {
        await sendGuestOtp();
        setMessage("No WhatsApp detected. OTP sent via SMS.");
      } catch (guestError) {
        throw new Error(guestError.message || whatsappError || "Unable to send OTP.");
      }
    } catch (err) {
      setError(err.message || "Unable to send OTP.");
    } finally {
      setIsSending(false);
    }
  };

  const verifyOtp = async () => {
    const normalizedOtp = String(otp).replace(/\D/g, "").slice(0, expectedOtpLength);
    if (normalizedOtp.length !== expectedOtpLength) {
      setError(`Please enter a valid ${expectedOtpLength}-digit OTP.`);
      return;
    }

    if (otpChannel === "whatsapp" && !sessionId) {
      setError("Please send OTP first.");
      return;
    }

    setIsVerifying(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(
        otpChannel === "guest" ? "/api/verifyGuestOtp" : "/otp/verify-whatsapp",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            otpChannel === "guest"
              ? {
                  mobile: normalizedPhone,
                  otp: normalizedOtp,
                }
              : {
                  sessionId,
                  phone: normalizedPhone,
                  otp: normalizedOtp,
                }
          ),
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, "Unable to verify OTP."));
      }

      setIsOtpVerified(true);
      setMessage("Phone verified successfully.");
    } catch (err) {
      setIsOtpVerified(false);
      setError(err.message || "Unable to verify OTP.");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className={className}>
      {inlinePhoneField ? (
        <>
          <div className="flex items-center">
            {!!countryCodePrefix && (
              <span className="flex items-center p-2 bg-gray-100 border border-r-0 rounded-l text-gray-700 select-none text-sm">
                {countryCodePrefix}
              </span>
            )}
            <div className="relative flex-1">
              <input
                name={phoneInputName}
                type="tel"
                maxLength={10}
                pattern="[0-9]{10}"
                placeholder={phonePlaceholder}
                className={`${countryCodePrefix ? "border-l-0 rounded-r" : "rounded"} w-full p-2 border text-[#000] focus:outline-none text-sm sm:text-base pr-24 ${phoneInputClassName}`}
                value={phone}
                disabled={otpEnabled && isOtpVerified}
                onChange={(e) => {
                  const nextValue = e.target.value.replace(/\D/g, "").slice(0, 10);
                  if (typeof onPhoneChange === "function") {
                    onPhoneChange(nextValue);
                  }
                }}
                required
              />
              {otpEnabled && (
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={isSending || normalizedPhone.length !== 10 || isOtpVerified}
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-xs rounded bg-[#0e605b] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSending ? "..." : isOtpSent ? (otpChannel === "guest" ? "Resend SMS" : "Resend") : "Send OTP"}
                </button>
              )}
            </div>
          </div>

          {!otpEnabled && (
            <p className={`mt-1 text-xs ${normalizedPhone.length === 10 ? "text-green-700" : "text-gray-600"}`}>
              {normalizedPhone.length === 10 ? "Phone number accepted" : "Enter a valid 10-digit phone number"}
            </p>
          )}

          {otpEnabled && isOtpVerified && <p className="mt-1 text-xs font-semibold text-green-700">Phone verified</p>}

          {otpEnabled && isOtpSent && !isOtpVerified && (
            <div className="mt-2 relative">
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, expectedOtpLength))}
                placeholder={`Enter ${expectedOtpLength}-digit OTP`}
                className="w-full p-2 border rounded text-[#000] focus:outline-none text-sm sm:text-base pr-20"
              />
              <button
                type="button"
                onClick={verifyOtp}
                disabled={isVerifying || otp.length !== expectedOtpLength}
                className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-xs rounded bg-[#ff9245] text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isVerifying ? "..." : "Verify"}
              </button>
            </div>
          )}

          {otpEnabled && !!error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          {otpEnabled && !!message && !error && <p className="mt-1 text-xs text-green-700">{message}</p>}
          {otpEnabled && renderSmsResendLink()}
        </>
      ) : (
        <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-2">Phone {otpEnabled ? "verification" : ""}</p>

          {otpEnabled ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={sendOtp}
                disabled={isSending || normalizedPhone.length !== 10 || isOtpVerified}
                className="px-3 py-2 text-sm rounded-md bg-[#0e605b] text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSending
                  ? "Sending..."
                  : isOtpSent
                    ? otpChannel === "guest"
                      ? "Resend SMS OTP"
                      : "Resend OTP on WhatsApp"
                    : "Send OTP on WhatsApp"}
              </button>

              {isOtpVerified && (
                <span className="text-sm font-semibold text-green-700 self-center">Verified</span>
              )}
            </div>
          ) : (
            <p className={`text-xs ${normalizedPhone.length === 10 ? "text-green-700" : "text-gray-600"}`}>
              {normalizedPhone.length === 10 ? "Phone number accepted" : "Enter a valid 10-digit phone number"}
            </p>
          )}

          {otpEnabled && isOtpSent && !isOtpVerified && (
            <div className="mt-3 w-full sm:w-72">
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, expectedOtpLength))}
                  placeholder={`Enter ${expectedOtpLength}-digit OTP`}
                  className="w-full pr-24 pl-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={isVerifying || otp.length !== expectedOtpLength}
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-xs rounded bg-[#ff9245] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isVerifying ? "..." : "Verify"}
                </button>
              </div>
              {renderSmsResendLink()}
            </div>
          )}

          {otpEnabled && !!error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          {otpEnabled && !!message && !error && <p className="mt-2 text-xs text-green-700">{message}</p>}
        </div>
      )}
    </div>
  );
}
